"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { CalendarPlus, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  STUDY_PLAN_LABELS,
  type StudyPlan,
  type StudyPlanId,
} from "@/lib/study-plan";
import {
  buildScheduledTasks,
  type PlanDynamicState,
  type ScheduledTask,
} from "@/lib/study-plan-state";
import type { SubscribedPlanMeta } from "./useScheduleData";
import MonthGrid from "./MonthGrid";
import { PLAN_CHIP } from "./schedule-shared";

/**
 * 구독 0개 상태 — 정규 날짜 기준 캘린더 미리보기 + "내 일정으로 시작" CTA (JCG-2310).
 *
 * Starter+ 인데 아직 계획을 시작하지 않은 사용자에게, 계획의 정규(회차) 일정을 월 그리드로
 * 미리 보여주고 계획 페이지 일정 그대로(정규 시작일 = Day 1) 시작하도록 유도한다(JCG-3652). 미리보기는 미구독 기본 상태
 * (defaultDynamic: 정규 시작일·오버라이드 없음)로 buildScheduledTasks 해 만든다 — 읽기 전용.
 */
export default function StartPlanCTA({
  startable,
  todayISO,
  onStart,
}: {
  startable: SubscribedPlanMeta[];
  todayISO: string;
  onStart: (planId: StudyPlanId) => Promise<boolean>;
}) {
  const [previewId, setPreviewId] = useState<StudyPlanId | null>(
    startable[0]?.planId ?? null,
  );

  const activePreview = useMemo(
    () => startable.find((s) => s.planId === previewId) ?? startable[0] ?? null,
    [startable, previewId],
  );

  if (!activePreview) {
    // 계획 정의가 전혀 없을 때(이론적). 안전망 안내.
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          현재 시작할 수 있는 학습 계획이 없어요.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 시작 안내 배너 — 형제 안내 카드와 동일한 연한 amber 틴트(dark 페어). */}
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40 sm:p-5">
        <div className="flex items-start gap-3">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-900/60 dark:text-amber-300"
            aria-hidden
          >
            <Sparkles className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-bold text-amber-900 dark:text-amber-200">
              학습 계획을 내 일정으로 시작해 보세요
            </h2>
            <p className="mt-0.5 text-xs text-amber-800/90 dark:text-amber-300/90">
              계획 페이지의 일정이 그대로 이 캘린더에 채워지고, 출석·완료가 함께 기록돼요. 일찍 시작했거나 늦었다면 ‘오늘 기준 다시 분배’로 맞출 수 있어요.
            </p>

            {/* 계획별 시작 버튼 */}
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              {startable.map(({ planId, plan }) => (
                <StartButton
                  key={planId}
                  planId={planId}
                  plan={plan}
                  isPreviewing={planId === activePreview.planId}
                  onPreview={() => setPreviewId(planId)}
                  onStart={onStart}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 정규 날짜 기준 미리보기(읽기 전용 월 그리드) */}
      <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[11px] font-bold",
              PLAN_CHIP[activePreview.planId],
            )}
          >
            {STUDY_PLAN_LABELS[activePreview.planId]}
          </span>
          <span className="text-[11px] text-muted-foreground">
            {activePreview.plan.examSessionLabel} 정규 일정 미리보기
          </span>
        </div>
        <PlanPreview plan={activePreview.plan} todayISO={todayISO} />
      </div>
    </div>
  );
}

function StartButton({
  planId,
  plan,
  isPreviewing,
  onPreview,
  onStart,
}: {
  planId: StudyPlanId;
  plan: StudyPlan;
  isPreviewing: boolean;
  onPreview: () => void;
  onStart: (planId: StudyPlanId) => Promise<boolean>;
}) {
  const [pending, setPending] = useState(false);

  const handleStart = async () => {
    if (pending) return;
    setPending(true);
    try {
      await onStart(planId);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        size="sm"
        onClick={() => void handleStart()}
        disabled={pending}
        className="bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-500 dark:text-amber-950 dark:hover:bg-amber-400"
      >
        <CalendarPlus className="mr-1.5 h-4 w-4" aria-hidden />
        {pending ? "시작하는 중…" : `${STUDY_PLAN_LABELS[planId]} 시작`}
      </Button>
      <button
        type="button"
        onClick={onPreview}
        aria-pressed={isPreviewing}
        className={cn(
          "rounded text-[11px] underline-offset-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
          isPreviewing
            ? "font-semibold text-amber-800 dark:text-amber-300"
            : "text-amber-700/80 hover:underline dark:text-amber-300/80",
        )}
      >
        미리보기
      </button>
      <Link
        href={plan.pagePath}
        className="rounded text-[11px] text-muted-foreground underline-offset-2 transition-colors hover:text-amber-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:text-amber-400"
      >
        계획 보기
      </Link>
    </div>
  );
}

/** 미구독 기본 상태로 정규 일정을 월 그리드에 읽기 전용 렌더. */
function PlanPreview({ plan, todayISO }: { plan: StudyPlan; todayISO: string }) {
  const tasksByDate = useMemo(() => {
    const dyn: PlanDynamicState = {
      subscribed: false,
      startDateISO: plan.startDateISO,
      overrides: {},
      attendedAt: {},
    };
    const tasks = buildScheduledTasks(plan, dyn);
    const map = new Map<string, ScheduledTask[]>();
    for (const t of tasks) {
      const arr = map.get(t.effectiveDateISO);
      if (arr) arr.push(t);
      else map.set(t.effectiveDateISO, [t]);
    }
    return map;
  }, [plan]);

  return (
    // 읽기 전용 미리보기 — inert 로 서브트리를 탭 순서·포인터·접근성 트리에서 제외한다
    // (MonthGrid 의 셀 <button> 이 포커스만 받고 아무 동작도 안 하는 a11y 함정 방지).
    <div inert className="select-none">
      <MonthGrid
        monthAnchorISO={plan.startDateISO}
        todayISO={todayISO}
        tasksByDate={tasksByDate}
        rangeMinISO={plan.startDateISO}
        rangeMaxISO={plan.examDateISO}
        onOpenDay={() => {}}
      />
    </div>
  );
}
